// vuex.js store for Library

//// IMPORTS
import APPS from '@generio-ai/library-legacy/constants/apps'
import ASSETS from '@generio-ai/library-legacy/constants/assets'
import LEVELS from '@generio-ai/library-legacy/constants/levels'


//// CONSTANTS

// mode
const MODE_FLOW = 'flows'
const MODE_MODEL = 'model'
const MODE_IMAGE = 'images'
const MODE_DEFAULT = MODE_FLOW


// sorting
const SORT_TYPE_DATE = 'date'
const SORT_TYPE_RATING = 'rating'
const SORT_TYPE_DEFAULT = SORT_TYPE_DATE
const SORT_ORDER_ASCENDING = 'ascending'
const SORT_ORDER_DESCENDING = 'descending'
const SORT_ORDER_DEFAULT = SORT_ORDER_DESCENDING


//// HELPER

// create state
const createState = () => {
    return {
        mode: MODE_DEFAULT,
        searchQuery: '',
        sortingType: SORT_TYPE_DEFAULT,
        sortingOrder: SORT_ORDER_DEFAULT,
    }
}


//// VUEX STORE
const library = {
    namespaced: true,
    state: createState(),
    mutations: {
        RESET(state) {
            Object.assign(state, createState())
        },

        SET_MODE(state, mode) {
            state.mode = mode
        },
        SET_SEARCH_QUERY(state, searchQuery) {
            state.searchQuery = searchQuery
        },
        SET_SORTING_TYPE(state, sortingType) {
            state.sortingType = sortingType
        },
        SET_SORTING_ORDER(state, sortingOrder) {
            state.sortingOrder = sortingOrder
        }
    },
    getters: {
        keyModeDefault: () => MODE_DEFAULT,
        keyModeFlow: () => MODE_FLOW,
        keyModeModel: () => MODE_MODEL,
        keyModeImage: () => MODE_IMAGE,
        
        keySortTypeDate: () => SORT_TYPE_DATE,
        keySortTypeRating: () => SORT_TYPE_RATING,

        keySortOrderAscending: () => SORT_ORDER_ASCENDING,
        keySortOrderDescending: () => SORT_ORDER_DESCENDING,

        hasSearchQuery: state => state.searchQuery && state.searchQuery.length > 0,

        getAllAssets: (state, getters, rootState, rootGetters) => {
            return rootGetters['flows/getAllAssets']
        },
        getFilteredAssets: (state, getters, rootState, rootGetters) => {
            // get array of assets
            let assets = [...getters.getAllAssets]
            if (!assets) return []

            // apply search query
            const searchQuery = getters.getSearchQuery
            const optimizedQuery = searchQuery ? searchQuery.trim().toLowerCase() : null
            if (optimizedQuery) {
                assets = assets.filter(asset => rootGetters['flows/queryAsset'](asset, optimizedQuery))
            }

            // get sorting
            const sortingType = getters.getSortingType
            const sortingOrder = getters.getSortingOrder

            // sort by date
            if (sortingType === SORT_TYPE_DATE) 
            {
                if (sortingOrder === SORT_ORDER_DESCENDING) {
                    assets.sort((a, b) => b.created - a.created)
                } else {
                    assets.sort((a, b) => a.created - b.created)
                }
            } 

            // sort by type
            else if (sortingType === SORT_TYPE_RATING) 
            {
                if (sortingOrder === SORT_ORDER_DESCENDING) {
                    assets.sort((a, b) => 
                        (b.additional?.rating || 0) - (a.additional?.rating || 0)
                    )
                } else {
                    assets.sort((a, b) => 
                        (a.additional?.rating || 0) - (b.additional?.rating || 0)
                    )
                }
            }

            return assets
        },
        getFilteredAssetsByFlow: (state, getters, rootState, rootGetters) => {

            // group assets by flow using a map (maintains insertion order)
            const assetsGrouped = new Map()

            // get assets for all relevant apps
            let assets = getters.getFilteredAssets.filter(
                asset => (asset.app === APPS.SKETCH && asset.additional?.count >= 0) ||
                         ([APPS.CREATE, APPS.MODIFY, APPS.SKETCH2, APPS.AGENT].includes(asset.app) && asset.additional?.flow >= 0)
            )

            // iterate through all assets
            for (const asset of assets) {

                let flow = null

                // if app sketch
                if (asset.app === APPS.SKETCH) 
                    flow = asset.additional?.count
                else
                    flow = asset.additional?.flow 
      
                if (!assetsGrouped.has(flow)) {
                    assetsGrouped.set(flow, [])
                }
                assetsGrouped.get(flow).push(asset)
            }
        
            // transform into an array of groups
            const assetsList = Array.from(assetsGrouped, ([flow, groupAssets]) => ({
                flow: parseInt(flow),
                assets: groupAssets,
            }))
        
            return assetsList
        },

        getFilteredImageAssets: (state, getters, rootState, rootGetters) => {
            return getters.getFilteredAssets.filter(
                asset => asset.type === ASSETS.TYPES.IMAGE &&
                asset.additional?.prompt?.dual === false
            )
        },
        getFilteredModelAssets: (state, getters, rootState, rootGetters) => {
            return getters.getFilteredAssets.filter(asset => asset.type === ASSETS.TYPES.MODEL)
        },

        getMode: state => state.mode,
        getSearchQuery: state => state.searchQuery,
        getSortingType: state => state.sortingType,
        getSortingOrder: state => state.sortingOrder,

        getRecentPrompts: (state, getters, rootState, rootGetters) => {
            const assets = rootGetters['assets/getAssets']
            if (!assets) return []
            const textAssets = assets.filter(asset => asset.type === ASSETS.TYPES.TEXT)
            textAssets.sort((a, b) => b.created - a.created)
            const prompts = []
            for (let i=0; i<textAssets.length; i++) {
                const asset = textAssets[i]
                const prompt = asset.additional?.prompt?.user || asset.additional?.prompt?.positive
                if (prompt && prompt.length > 0 && !prompts.includes(prompt)) {
                    prompts.push(prompt)
                    if (prompts.length >= 5) break
                }
            }
            return prompts
        }
    },
    actions: {
        resetLibrary({ commit }) {
            commit('RESET')
        },

        setMode({ commit }, mode) {
            commit('SET_MODE', mode)
        },
        setSearchQuery({ commit }, searchQuery) {
            commit('SET_SEARCH_QUERY', searchQuery)
        },
        setSortingType({ commit }, sortingType) {
            commit('SET_SORTING_TYPE', sortingType)
        },
        setSortingOrder({ commit }, sortingOrder) {
            commit('SET_SORTING_ORDER', sortingOrder)
        },




        // TODO move below to assets store

        // rate asset
        async rateAsset({ dispatch }, {
            asset,
            rating
        })
        {
            // ensure asset parameter is provided
            if (asset === null || asset === undefined) {
                throw new Error("Please provide an asset to rate.")
            }

            try
            {
                // activate loading overlay
                dispatch('overlay/show', undefined, { root: true })

                // call route to rate asset
                const response = await dispatch('assets/updateAdditional', {
                    asset: asset,
                    additional: {
                        rating
                    }
                }, { root: true })

                // deactivate loading overlay
                dispatch('overlay/hide', undefined, { root: true })

                // log event
                dispatch('analytics/logEvent', { 
                    name: "Asset Rating", 
                    description: "User has rated the asset: " + asset.id
                }, { root: true })

                return response
            }
            catch(error)
            {
                // deactivate loading overlay
                dispatch('overlay/hide', undefined, { root: true })

                // log bug
                dispatch('analytics/logError', { 
                    name: "Asset Rating",
                    description: "Rating update failed for asset: " + asset.id
                }, { root: true })

                // provide user feedback
                dispatch('notifications/show', { 
                    message: 'Asset rating failed to update.', 
                    level: LEVELS.ERROR
                }, { root: true })

                throw error
            }
        },

        // download asset
        async downloadAsset({ dispatch }, {
            asset,
            key = 'default',
            format = null
        })
        {
            // ensure asset parameter is provided
            if (asset === null || asset === undefined) {
                throw new Error("Please provide an asset to download.")
            }

            try
            {
                // activate loading overlay
                dispatch('overlay/show', undefined, { root: true })

                // call route to download asset
                const response = await dispatch('assets/downloadFile', {
                    asset,
                    fileKey: key,
                    format
                }, { root: true })

                // deactivate loading overlay
                dispatch('overlay/hide', undefined, { root: true })

                // provide user feedback
                dispatch('notifcations/show', { 
                    message: 'Downloading the asset was successful.'
                }, { root: true })

                return response
            }
            catch(error)
            {
                // deactivate loading overlay
                dispatch('overlay/hide', undefined, { root: true })

                // provide user feedback
                 dispatch('notification/show', { 
                    message: 'Downloading the asset has failed.', 
                    level: LEVELS.ERROR
                }, { root: true })

                throw error
            }
        },

        // delete asset 
        async deleteAsset({ dispatch }, {
            asset,
            silent = false
        })
        {
            // ensure asset parameter is provided
            if (asset === null || asset === undefined) {
                throw new Error("Please provide an asset to delete.")
            }

            try
            {
                // activate loading overlay
                if (!silent)
                    dispatch('overlay/show', undefined, { root: true })

                // call route to delete asset
                const response = await dispatch('assets/delete', {
                    id: asset.id
                }, { root: true })

                // deactivate loading overlay
                if (!silent)
                    dispatch('overlay/hide', undefined, { root: true })

                // provide user feedback
                if (!silent)
                    dispatch('notifications/show', { 
                        message: 'Deleting the asset was successful.'
                    }, { root: true })

                return response
            }
            catch(error)
            {
                // deactivate loading overlay
                if (!silent)
                    dispatch('overlay/hide', undefined, { root: true })

                // provide user feedback
                if (!silent)
                    dispatch('notifications/show', { 
                        message: 'Deleting the asset has failed.', 
                        level: LEVELS.ERROR
                    }, { root: true })

                throw error
            }
        },

        // delete assets
        async deleteAssets({ dispatch }, assets)
        {
            // ensure assets parameter is provided
            if (assets === null || assets === undefined) {
                throw new Error("Please provide a list of assets to delete.")
            }

            try
            {
                // iterate through all assets
                for (let asset of assets)
                {
                    // delete asset
                    dispatch('deleteAsset', { asset, silent: true })
                }
            }
            catch(error)
            {
                throw error
            }
        }
    }
}

export default library
